/**
 * Derive the URL of the main digital-twin agent (the tenant root host) from a
 * satellite app's current location.
 *
 * Every satellite app under `apps/<name>/` is served at
 * `<name>.<env>.tricorder.icu` (e.g. `care-planner.usb.tricorder.icu`); the main digital
 * twin lives at the PARENT host (`usb.tricorder.icu`). A user who lands on a
 * satellite app must ALWAYS be able to get back to the twin (Joël's
 * requirement), so this is the single source of truth for that navigation
 * target — used by the always-on `ReturnToMainAgent` button and by each
 * satellite app's login redirect.
 *
 * Co-located with `baseUrl.ts`, its sibling: both read the build-injected
 * `VITE_APP_SUBDOMAIN` (= the `apps/<name>` dir name, set by
 * `frontend/vite-app-preset.ts`) to strip the app's own subdomain prefix.
 * `baseUrl.ts` strips it to reach the parent API host; this strips it to reach
 * the parent twin app. Living here (not in a shared dir outside `frontend/`)
 * keeps npm + type
 * resolution working — satellite apps already import shared UI from
 * `@frontend/...`; this follows the same path. The login redirects that used to
 * hand-roll a per-app regex + dev-port literal now import from here.
 *
 * We strip exactly the app's OWN subdomain — DNS hierarchy is the directory, so
 * there is no enumerated list of sibling apps to maintain (one such list, missing
 * `knowledge-profile.`, caused the `care-planner.knowledge-profile.…` agent-hop bug; sessionRouting.ts now
 * derives from here too). A new sub-app needs no entry; its own VITE_APP_SUBDOMAIN
 * is all this needs.
 *
 * No hardcoded domain: the parent host is the current hostname minus that prefix.
 * Protocol is preserved. Dev ports map to the main app's dev port via
 * the shared dev-port module. The target path is always the twin root "/" — the satellite path
 * is intentionally discarded (the user wants the agent, not a path echo across
 * origins).
 */

import { MAIN_DEV_PORT, SUB_APP_DEV_PORTS } from '@/lib/devPorts';

/**
 * Strip THIS app's own subdomain label to reach the parent (twin) host.
 *
 * Each build injects its own label via `VITE_APP_SUBDOMAIN` (the `apps/<name>`
 * dir, set by frontend/vite-app-preset.ts), and the platform convention is
 * `apps/<name>` ⇒ `<name>.<env>`, so the parent is simply "current host minus my
 * own prefix" — DNS hierarchy as the directory, no sibling-app list. Same logic
 * as `baseUrl.ts`. The main app (`VITE_APP_SUBDOMAIN=""`) and a host that doesn't
 * start with the app's own prefix are returned unchanged — a safe no-op, never a
 * wrong cross-origin jump. Pure (the env read defaults the param, so a test can
 * pass an explicit subdomain or stub `VITE_APP_SUBDOMAIN`).
 */
export function deriveMainHost(
  hostname: string,
  ownSubdomain: string = import.meta.env.VITE_APP_SUBDOMAIN ?? '',
): string {
  if (!ownSubdomain) return hostname;
  const prefix = `${ownSubdomain}.`;
  return hostname.startsWith(prefix) ? hostname.slice(prefix.length) : hostname;
}

/**
 * Map a satellite dev port to the main app's dev port (no-op in production,
 * where SUB_APP_DEV_PORTS is empty and there is no port to begin with).
 *
 * Reads MAIN_DEV_PORT from the shared module rather than its own literal: this
 * function used to hardcode '5173' while sessionRouting.ts honoured
 * VITE_MAIN_DEV_PORT, so setting that var moved one direction of the hop and
 * not the other.
 */
function mainPort(port: string): string {
  return SUB_APP_DEV_PORTS.has(port) ? MAIN_DEV_PORT : port;
}

/**
 * Build the absolute URL of a route on the main digital-twin agent for the
 * current location. Defaults to the twin root ("/"); pass a path (e.g.
 * "/patients") to deep-link into a specific main-app route from a satellite —
 * used by the cross-origin `MainAppNav` header icons. Pure aside from reading
 * `window.location`.
 */
export function mainAppUrl(path: string = '/'): string {
  const { protocol, hostname, port } = window.location;
  const host = deriveMainHost(hostname);
  const p = mainPort(port);
  const portSuffix = p ? `:${p}` : '';
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  return `${protocol}//${host}${portSuffix}${normalisedPath}`;
}

/**
 * Build the main app's login URL with a `returnTo` back to the satellite app's
 * current full URL, so the user lands back where they started after auth.
 * Replaces the per-app login-redirect logic in each `App.tsx`.
 */
export function mainAppLoginUrl(): string {
  const base = mainAppUrl().replace(/\/$/, '');
  const returnTo = encodeURIComponent(window.location.href);
  return `${base}/login?returnTo=${returnTo}`;
}
